<template>
    <div class="admin-paper-upload-dashboard animation-fadeIn">
        <!-- Page Header -->
        <div class="page-header">
            <div class="header-info">
                <h2 class="page-title">上传原始试卷</h2>
                <p class="page-subtitle">直接上传试卷原始文件（PDF/Word/压缩包），供用户整卷下载或由录入员后期处理</p>
            </div>
            <div class="header-actions">
                <el-button @click="$router.back()">
                    <el-icon>
                        <ArrowLeft />
                    </el-icon> 返回列表
                </el-button>
            </div>
        </div>

        <div class="upload-layout">
            <el-row :gutter="24">
                <!-- Main Form Column -->
                <el-col :lg="14" :md="24">
                    <el-card shadow="never" class="modern-card settings-card">
                        <template #header>
                            <div class="card-header">
                                <span class="card-title"><el-icon>
                                        <Document />
                                    </el-icon> 试卷元数据</span>
                            </div>
                        </template>

                        <el-form :model="paperForm" label-position="top" class="modern-form">
                            <el-form-item label="试卷完整名称" required>
                                <el-input v-model="paperForm.name" placeholder="请输入完整试卷名称（如：2024年上海市静安区高一数学期末试卷）" />
                            </el-form-item>

                            <el-row :gutter="16">
                                <el-col :span="8">
                                    <el-form-item label="试卷分类" required>
                                        <el-select v-model="paperForm.category" style="width: 100%">
                                            <el-option label="同步卷" value="同步卷" />
                                            <el-option label="月考卷" value="月考卷" />
                                            <el-option label="期中卷" value="期中卷" />
                                            <el-option label="期末卷" value="期末卷" />
                                            <el-option label="模拟卷" value="模拟卷" />
                                            <el-option label="真题卷" value="真题卷" />
                                        </el-select>
                                    </el-form-item>
                                </el-col>
                                <el-col :span="8">
                                    <el-form-item label="年份">
                                        <el-input v-model="paperForm.year" placeholder="2024" />
                                    </el-form-item>
                                </el-col>
                                <el-col :span="8">
                                    <el-form-item label="学期">
                                        <el-select v-model="paperForm.semester" style="width: 100%">
                                            <el-option label="上学期" value="上学期" />
                                            <el-option label="下学期" value="下学期" />
                                        </el-select>
                                    </el-form-item>
                                </el-col>
                            </el-row>

                            <el-row :gutter="16">
                                <el-col :span="8">
                                    <el-form-item label="年级">
                                        <el-select v-model="paperForm.grade" style="width: 100%">
                                            <el-option label="高一" value="高一" />
                                            <el-option label="高二" value="高二" />
                                            <el-option label="高三" value="高三" />
                                        </el-select>
                                    </el-form-item>
                                </el-col>
                                <el-col :span="8">
                                    <el-form-item label="难度">
                                        <el-select v-model="paperForm.difficulty" style="width: 100%">
                                            <el-option label="容易" value="容易" />
                                            <el-option label="较易" value="较易" />
                                            <el-option label="适中" value="适中" />
                                            <el-option label="较难" value="较难" />
                                            <el-option label="困难" value="困难" />
                                        </el-select>
                                    </el-form-item>
                                </el-col>
                                <el-col :span="8">
                                    <el-form-item label="所属地区">
                                        <el-cascader v-model="paperForm.area" :options="pcTextArr" placeholder="请选择"
                                            style="width: 100%" clearable expand-trigger="hover" />
                                    </el-form-item>
                                </el-col>
                            </el-row>

                            <el-form-item label="来源/出题单位">
                                <el-input v-model="paperForm.source" placeholder="例如：某某中学教研组" />
                            </el-form-item>

                            <!-- Textbook fields -->
                            <el-row :gutter="16">
                                <el-col :span="12">
                                    <el-form-item label="教材版本 (可选)">
                                        <el-select v-model="paperForm.textbookVersionId" style="width: 100%"
                                            placeholder="请选择教材版本" clearable @change="onTextbookVersionChange">
                                            <el-option v-for="tv in textbookVersions" :key="tv.id" :label="tv.name"
                                                :value="tv.id" />
                                        </el-select>
                                    </el-form-item>
                                </el-col>
                                <el-col :span="12">
                                    <el-form-item label="教材 (可选)">
                                        <el-select v-model="paperForm.textbookId" style="width: 100%"
                                            placeholder="请先选择教材版本" clearable
                                            :disabled="!paperForm.textbookVersionId || textbooks.length === 0">
                                            <el-option v-for="tb in textbooks" :key="tb.id" :label="tb.name"
                                                :value="tb.id" />
                                        </el-select>
                                    </el-form-item>
                                </el-col>
                            </el-row>

                            <el-form-item label="详细描述 (可选)">
                                <el-input v-model="paperForm.description" type="textarea" :rows="3"
                                    placeholder="请输入试卷相关补充说明..." />
                            </el-form-item>
                        </el-form>
                    </el-card>
                </el-col>

                <!-- Sidebar column for Upload & Preview -->
                <el-col :lg="10" :md="24">
                    <el-card shadow="never" class="modern-card upload-section">
                        <template #header>
                            <div class="card-header">
                                <span class="card-title"><el-icon>
                                        <Upload />
                                    </el-icon> 文件上传</span>
                                <el-tag type="info" size="small" effect="plain">支持 Word, PDF, 压缩包</el-tag>
                            </div>
                        </template>

                        <div class="refined-upload-area">
                            <el-upload ref="uploadRef" class="modern-dragger" drag :auto-upload="false" :limit="1"
                                :on-change="handleFileChange" :on-exceed="handleExceed"
                                accept=".docx,.doc,.pdf,.zip,.rar">
                                <div v-if="!uploadedFile" class="upload-empty-state">
                                    <el-icon class="large-upload-icon">
                                        <UploadFilled />
                                    </el-icon>
                                    <div class="main-text">点击或将文件拖拽到此处上传</div>
                                    <div class="sub-text">单文件最大支持 50MB</div>
                                </div>
                                <div v-else class="file-present-state">
                                    <el-icon :size="48" :color="getFileIconColor(uploadedFile.name)"
                                        class="file-main-icon">
                                        <component :is="getFileIcon(uploadedFile.name)" />
                                    </el-icon>
                                    <div class="file-metas">
                                        <div class="fname">{{ uploadedFile.name }}</div>
                                        <div class="fsize">{{ formatFileSize(uploadedFile.size) }}</div>
                                    </div>
                                    <el-button type="danger" circle link @click.stop="removeFile" class="remove-btn">
                                        <el-icon>
                                            <Delete />
                                        </el-icon>
                                    </el-button>
                                </div>
                            </el-upload>

                            <!-- Progress Indicator -->
                            <div v-if="uploading" class="upload-progress-box">
                                <div class="progress-label">文件上传中... {{ uploadProgress }}%</div>
                                <el-progress :percentage="uploadProgress" :stroke-width="8"
                                    :status="uploadProgress === 100 ? 'success' : ''" />
                            </div>

                            <!-- Success Banner -->
                            <div v-if="uploadedUrl" class="upload-success-ready animation-fadeIn">
                                <el-alert :closable="false" type="success" show-icon title="文件已成功传输至服务器">
                                    文件路径：{{ paperForm.fileName }}
                                </el-alert>
                            </div>
                        </div>

                        <div class="card-footer-actions">
                            <el-button type="primary" size="large" @click="uploadFile" :loading="uploading"
                                :disabled="!uploadedFile || uploading" v-if="!uploadedUrl" block class="action-btn">
                                <el-icon>
                                    <Upload />
                                </el-icon> 开始上传
                            </el-button>
                            <el-button type="success" size="large" @click="savePaper" :loading="saving"
                                :disabled="!uploadedUrl || saving" v-if="uploadedUrl" block class="action-btn">
                                <el-icon>
                                    <CircleCheck />
                                </el-icon> 确认保存试卷
                            </el-button>
                        </div>
                    </el-card>

                    <!-- Manual Images Upload Card (NEW) -->
                    <el-card shadow="never" class="modern-card image-upload-section" style="margin-top: 24px">
                        <template #header>
                            <div class="card-header">
                                <span class="card-title"><el-icon>
                                        <Picture />
                                    </el-icon> 手动上传预览图</span>
                                <el-tag type="warning" size="small" effect="plain">建议：手动转为图片后上传，效果最佳</el-tag>
                            </div>
                        </template>

                        <div class="manual-image-area">
                            <el-upload action="/api/upload/image" list-type="picture-card" :file-list="manualImageList"
                                :headers="uploadHeaders" :data="{ type: 'paper' }"
                                :on-preview="handlePictureCardPreview" :on-remove="handleImageRemove"
                                :on-success="handleImageSuccess" multiple accept=".png,.jpg,.jpeg">
                                <el-icon>
                                    <Plus />
                                </el-icon>
                            </el-upload>
                            <p class="upload-tip">您可以上传多张对应试卷页面的图片，将自动按上传顺序排序</p>
                        </div>
                    </el-card>

                    <el-dialog v-model="dialogVisible" title="图片预览" width="50%">
                        <img :src="dialogImageUrl" alt="Preview Image" style="width: 100%" />
                    </el-dialog>

                    <!-- Image Preview Card -->
                    <el-card v-if="uploadedUrl && paperForm.previewImages?.length" shadow="never"
                        class="modern-card preview-card" style="margin-top: 24px">
                        <template #header>
                            <div class="card-header">
                                <span class="card-title"><el-icon>
                                        <View />
                                    </el-icon> 试卷原卷预览</span>
                                <div class="header-tools">
                                    <span class="preview-badge">展示模式</span>
                                </div>
                            </div>
                        </template>
                        <div class="paper-preview-gallery">
                            <div v-for="(img, idx) in paperForm.previewImages" :key="idx" class="preview-page-box">
                                <el-image :src="img" lazy :preview-src-list="paperForm.previewImages"
                                    :initial-index="idx" class="preview-img">
                                    <template #placeholder>
                                        <div class="img-loading">加载中...</div>
                                    </template>
                                </el-image>
                                <div class="preview-page-num">第 {{ idx + 1 }} 页 / 共 {{ paperForm.previewImages.length }}
                                    页</div>
                            </div>
                        </div>
                    </el-card>

                    <!-- Fallback Preview Card (PDF/Others) -->
                    <el-card v-else-if="uploadedUrl && canPreview" shadow="never" class="modern-card preview-card"
                        style="margin-top: 24px">
                        <template #header>
                            <div class="card-header">
                                <span class="card-title"><el-icon>
                                        <View />
                                    </el-icon> 文件常规预览</span>
                                <div class="header-tools">
                                    <el-button size="small" plain @click="downloadFile"><el-icon>
                                            <Download />
                                        </el-icon></el-button>
                                </div>
                            </div>
                        </template>
                        <div class="iframe-container">
                            <iframe :src="previewUrl" width="100%" height="450px"></iframe>
                        </div>
                    </el-card>
                </el-col>
            </el-row>
        </div>
    </div>
</template>

<script setup>
import { ref, reactive, computed } from 'vue'
import { useRouter } from 'vue-router'
import { ElMessage } from 'element-plus'
import {
    UploadFilled, Delete, Document, Download, CircleCheck,
    Folder, Link, ArrowLeft, Upload, View, Picture, Plus
} from '@element-plus/icons-vue'
import { pcTextArr } from 'element-china-area-data'
import api from '@/utils/api'

const router = useRouter()
const uploadRef = ref(null)
const uploading = ref(false)
const saving = ref(false)
const uploadProgress = ref(0)
const uploadedFile = ref(null)
const uploadedUrl = ref('')
const manualImageList = ref([])
const dialogImageUrl = ref('')
const dialogVisible = ref(false)

const paperForm = reactive({
    name: '',
    description: '',
    category: '期中卷',
    year: new Date().getFullYear().toString(),
    semester: '上学期',
    grade: '高一',
    difficulty: '适中',
    source: '',
    area: [],
    fileUrl: '',
    fileType: '',
    fileName: '',
    previewImages: [],
    textbookVersionId: null,
    textbookId: null,
})

// Textbook selects
const textbookVersions = ref([])
const textbooks = ref([])

const fetchTextbookVersions = async () => {
    try {
        const res = await api.get('/api/textbook-versions')
        textbookVersions.value = res.data.versions || []
    } catch (e) {
        console.warn('Failed to load textbook versions', e)
    }
}

const onTextbookVersionChange = async (versionId) => {
    paperForm.textbookId = null
    textbooks.value = []
    if (!versionId) return
    try {
        const res = await api.get('/api/textbooks', { params: { versionId } })
        textbooks.value = res.data.textbooks || []
    } catch (e) {
        console.warn('Failed to load textbooks', e)
    }
}

fetchTextbookVersions()

const uploadHeaders = computed(() => {
    const token = localStorage.getItem('token')
    return token ? { Authorization: `Bearer ${token}` } : {}
})

const previewableTypes = ['pdf', 'docx', 'doc']

const canPreview = computed(() => {
    return uploadedUrl.value && previewableTypes.includes(paperForm.fileType?.toLowerCase())
})

const previewUrl = computed(() => {
    if (!uploadedUrl.value) return ''
    const fileType = paperForm.fileType?.toLowerCase()
    if (fileType === 'pdf') {
        return `https://docs.google.com/viewer?embedded=true&url=${encodeURIComponent(uploadedUrl.value)}`
    } else if (['docx', 'doc'].includes(fileType)) {
        return `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(uploadedUrl.value)}`
    }
    return uploadedUrl.value
})

const openInNewTab = () => {
    if (uploadedUrl.value) {
        window.open(uploadedUrl.value, '_blank')
    }
}

const handleImageSuccess = (response, uploadFile) => {
    if (response.success) {
        paperForm.previewImages.push(response.url)
        manualImageList.value.push({
            name: uploadFile.name,
            url: response.url
        })
    }
}

const handleImageRemove = (uploadFile) => {
    const urlToRemove = uploadFile.url
    paperForm.previewImages = paperForm.previewImages.filter(url => url !== urlToRemove)
    manualImageList.value = manualImageList.value.filter(item => item.url !== urlToRemove)
}

const handlePictureCardPreview = (uploadFile) => {
    dialogImageUrl.value = uploadFile.url
    dialogVisible.value = true
}

const handleFileChange = (file) => {
    uploadedFile.value = file.raw
    uploadedUrl.value = ''
    paperForm.fileUrl = ''
    paperForm.fileType = ''
    paperForm.fileName = ''
}

const handleExceed = () => {
    ElMessage.warning('只能上传一个文件，请先删除已选文件')
}

const removeFile = () => {
    uploadedFile.value = null
    uploadedUrl.value = ''
    paperForm.fileUrl = ''
    paperForm.fileType = ''
    paperForm.fileName = ''
    if (uploadRef.value) {
        uploadRef.value.clearFiles()
    }
}

const getFileIcon = (filename) => {
    if (!filename) return Document
    const ext = filename.split('.').pop()?.toLowerCase()
    if (['zip', 'rar'].includes(ext)) return Folder
    return Document
}

const getFileIconColor = (filename) => {
    if (!filename) return '#909399'
    const ext = filename.split('.').pop()?.toLowerCase()
    const colors = {
        'docx': '#2b579a',
        'doc': '#2b579a',
        'pdf': '#f40f02',
        'zip': '#f9a825',
        'rar': '#f9a825'
    }
    return colors[ext] || '#909399'
}

const formatFileSize = (bytes) => {
    if (!bytes) return '0 B'
    const k = 1024
    const sizes = ['B', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
}

const uploadFile = async () => {
    if (!uploadedFile.value) return
    uploading.value = true
    uploadProgress.value = 0
    const formData = new FormData()
    formData.append('file', uploadedFile.value)
    formData.append('type', 'paper')
    try {
        const progressInterval = setInterval(() => {
            if (uploadProgress.value < 85) {
                uploadProgress.value += Math.floor(Math.random() * 15)
            }
        }, 300)

        const res = await api.post('/api/papers/upload-file', formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
        })
        clearInterval(progressInterval)
        uploadProgress.value = 100
        if (res.data.success) {
            uploadedUrl.value = res.data.url
            paperForm.fileUrl = res.data.url
            paperForm.fileType = res.data.fileType
            paperForm.fileName = res.data.fileName
            // Only update if backend actually returns previews (though it's now disabled on server)
            if (res.data.previewImages && res.data.previewImages.length > 0) {
                paperForm.previewImages = res.data.previewImages
            }
            ElMessage.success('主文件已同步成功')
        } else {
            ElMessage.error(res.data.error || '上传失败')
        }
    } catch (error) {
        console.error('Upload error:', error)
        ElMessage.error('服务连接异常')
    } finally {
        uploading.value = false
    }
}

const savePaper = async () => {
    if (!paperForm.name) {
        ElMessage.warning('请填写试卷名称')
        return
    }
    saving.value = true
    try {
        const region = paperForm.area?.[0] || ''
        const city = paperForm.area?.[1] || ''
        const payload = {
            ...paperForm,
            region,
            city,
            // Send null instead of 0 to avoid FK violation
            textbookVersionId: paperForm.textbookVersionId || undefined,
            textbookId: paperForm.textbookId || undefined,
        }
        const res = await api.post('/api/papers/create-with-file', payload)
        if (res.data.id) {
            ElMessage.success('试卷信息录入完成')
            router.push('/admin/paper-list')
        } else {
            ElMessage.error(res.data.error || '保存失败')
        }
    } catch (error) {
        ElMessage.error('系统错误')
    } finally {
        saving.value = false
    }
}

const downloadFile = async () => {
    if (uploadedUrl.value) {
        window.open(uploadedUrl.value, '_blank')
    }
}
</script>

<style scoped>
.admin-paper-upload-dashboard {
    background-color: transparent;
}

.page-header {
    margin-bottom: 24px;
    display: flex;
    justify-content: space-between;
    align-items: center;
}

.page-title {
    margin: 0;
    font-size: 24px;
    font-weight: 700;
}

.page-subtitle {
    margin-top: 4px;
    color: #606266;
    font-size: 14px;
}

.modern-card {
    border: none;
    border-radius: 12px;
}

.card-title {
    font-size: 16px;
    font-weight: 700;
    display: flex;
    align-items: center;
    gap: 8px;
}

.modern-form :deep(.el-form-item__label) {
    font-weight: 600;
    padding-bottom: 4px;
}

.refined-upload-area {
    margin-bottom: 24px;
}

.modern-dragger :deep(.el-upload-dragger) {
    border: 2px dashed #dcdfe6;
    border-radius: 12px;
    background: #f8fafc;
    transition: all 0.3s;
    height: 180px;
    display: flex;
    align-items: center;
    justify-content: center;
}

.modern-dragger :deep(.el-upload-dragger):hover {
    border-color: #409eff;
    background: #ecf5ff;
}

.upload-empty-state {
    text-align: center;
}

.large-upload-icon {
    font-size: 48px;
    color: #909399;
    margin-bottom: 12px;
}

.main-text {
    font-weight: 600;
    color: #333;
}

.sub-text {
    font-size: 12px;
    color: #909399;
}

.file-present-state {
    display: flex;
    align-items: center;
    width: 100%;
    padding: 0 30px;
    text-align: left;
}

.file-metas {
    flex: 1;
    margin-left: 20px;
    overflow: hidden;
}

.fname {
    font-weight: 600;
    color: #333;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.fsize {
    font-size: 12px;
    color: #909399;
    margin-top: 4px;
}

.upload-progress-box {
    margin: 20px 0;
    padding: 15px;
    background: #fff;
    border-radius: 8px;
    border: 1px solid #f0f0f0;
}

.progress-label {
    font-size: 13px;
    color: #606266;
    margin-bottom: 10px;
}

.upload-success-ready {
    margin-top: 20px;
}

.card-footer-actions {
    display: flex;
    gap: 12px;
    margin-top: 20px;
}

.action-btn {
    flex: 1;
}

.iframe-container {
    border: 1px solid #e4e7ed;
    border-radius: 8px;
    overflow: hidden;
}

.animation-fadeIn {
    animation: fadeIn 0.4s ease-out;
}

@keyframes fadeIn {
    from {
        opacity: 0;
        transform: translateY(10px);
    }

    to {
        opacity: 1;
        transform: translateY(0);
    }
}

/* Paper Preview Gallery Styles */
.paper-preview-gallery {
    background: #f5f7fa;
    padding: 20px;
    border-radius: 8px;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 20px;
    max-height: 500px;
    overflow-y: auto;
}

.preview-page-box {
    background: white;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
    border-radius: 4px;
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    overflow: hidden;
}

.preview-img {
    width: 100%;
}

.preview-page-num {
    padding: 8px;
    font-size: 11px;
    color: #909399;
    border-top: 1px solid #f0f2f5;
    width: 100%;
    text-align: center;
}

.img-loading {
    height: 200px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #909399;
}

.preview-badge {
    font-size: 11px;
    background: #ecf5ff;
    color: #409eff;
    padding: 2px 8px;
    border-radius: 100px;
}

.manual-image-area {
    padding: 10px 0;
}

.upload-tip {
    font-size: 12px;
    color: #909399;
    margin-top: 12px;
}

:deep(.el-upload--picture-card) {
    width: 100px;
    height: 100px;
}

:deep(.el-upload-list--picture-card .el-upload-list__item) {
    width: 100px;
    height: 100px;
}
</style>
